import React, { useState, useRef, useEffect } from 'react';
import { postData } from '../../Api/Clientfunctions';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
    FaBriefcase, 
    FaCode, 
    FaChartLine, 
    FaCubes, 
    FaRocket, 
    FaUsers, 
    FaLaptopCode, 
    FaPaperPlane 
} from 'react-icons/fa';

const jobs = [
    {
        id: 1,
        title: "Senior Full Stack Developer",
        category: "Technology",
        icon: <FaCode />,
        type: "Full-time",
        location: "Jaipur / Remote",
        experience: "3+ Years",
        description: "Join our core team to build scalable Web3 and Metaverse solutions using Node.js, React, and Blockchain technologies."
    },
    {
        id: 2,
        title: "Frontend Developer (React)",
        category: "Technology",
        icon: <FaLaptopCode />,
        type: "Full-time",
        location: "Jaipur, India",
        experience: "1-3 Years",
        description: "Focus on creating high-performance, pixel-perfect user interfaces for our decentralized applications."
    },
    {
        id: 3,
        title: "Sales Executive",
        category: "Sales",
        icon: <FaChartLine />,
        type: "Full-time",
        location: "Jaipur / On-site",
        experience: "1-2 Years",
        description: "Drive growth by connecting with global clients and showcasing our innovative IT and Blockchain services."
    },
    {
        id: 4,
        title: "Blockchain Engineer",
        category: "Technology",
        icon: <FaCubes />,
        type: "Full-time",
        location: "Remote",
        experience: "2+ Years",
        description: "Develop smart contracts, DApps, and integrated blockchain solutions on Ethereum, BSC, and Polygon."
    },
    {
        id: 5,
        title: "Digital Marketing Specialist",
        category: "Marketing",
        icon: <FaRocket />,
        type: "Full-time",
        location: "Jaipur, India",
        experience: "2+ Years",
        description: "Manage our digital presence, SEO, and social media campaigns to elevate the Meta Block brand."
    }
];

const roleGroups = [
    { label: '💻 Development', options: ['Senior Full Stack Developer', 'Frontend Developer (React)', 'Backend Developer (Node.js)', 'Mobile App Developer (React Native)'] },
    { label: '⛓️ Blockchain', options: ['Blockchain Engineer', 'Smart Contract Developer', 'Web3 / DApp Developer'] },
    { label: '🎨 Design / UI-UX', options: ['UI/UX Designer', 'Product Designer', 'Graphic Designer'] },
    { label: '📈 Sales & Business', options: ['Sales Executive', 'Business Development Manager', 'Client Relationship Manager'] },
    { label: '📣 Marketing', options: ['Digital Marketing Specialist', 'SEO / Content Strategist', 'Social Media Manager'] },
    { label: '🔧 Other', options: ['Project Manager', 'HR Executive', 'Other / Not Listed'] },
];

const Career = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        position: '',
        linkedin: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Build a clean email body with all user data
            const subject = encodeURIComponent(`Job Application – ${formData.position || 'General Enquiry'}`);
            const body = encodeURIComponent(
`Hello Meta Block HR Team,

I am interested in applying for the position of: ${formData.position || 'N/A'}

--- Applicant Details ---
Name       : ${formData.name}
Email      : ${formData.email}
Phone      : ${formData.phone}
Position   : ${formData.position || 'N/A'}
Portfolio  : ${formData.linkedin || 'N/A'}

--- Cover Note ---
${formData.message || 'No message provided.'}

---
This application was submitted via the Meta Block Careers page.`
            );

            // Open user's email client with the form data pre-filled
            window.location.href = `mailto:info@metablocktechnologies.in?subject=${subject}&body=${body}`;

            // Also send to backend for logging (non-blocking)
            postData("/contact/submit-enquiry", { ...formData, type: 1, subject: `Career Application for ${formData.position}` })
                .catch(() => {}); // silently ignore backend errors

            toast.success("Your email client has opened! Please click Send to submit your application.", {
                position: "top-right",
                autoClose: 6000,
            });

            // Reset form
            setFormData({ name: '', email: '', phone: '', position: '', linkedin: '', message: '' });

        } catch (error) {
            toast.error("Something went wrong. Please email us directly at careers@metablock.in");
        } finally {
            setLoading(false);
        }
    };

    const scrollToForm = (position) => {
        setFormData(prev => ({ ...prev, position: position }));
        const formElement = document.getElementById('application-form');
        if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 overflow-x-hidden pt-20">
            <ToastContainer />
            
            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-blue-900 via-blue-950 to-indigo-950 py-24 md:py-36 text-white text-center overflow-hidden">
                {/* Background Decorative Circles */}
                <div className="absolute -top-20 -left-20 w-80 h-80 bg-blue-500/20 blur-[100px] rounded-full animate-pulse transition-all duration-1000"></div>
                <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] bg-indigo-500/10 blur-[120px] rounded-full animate-pulse delay-700 transition-all duration-1000"></div>
                
                <div className="container mx-auto px-4 relative z-10">
                    <span className="inline-block px-5 py-2 rounded-full bg-blue-500/20 border border-blue-400/30 text-blue-200 text-sm font-black tracking-widest uppercase mb-10 backdrop-blur-sm">
                        Career Opportunities
                    </span>
                    <h1 className="text-6xl md:text-9xl font-black mb-10 tracking-tighter leading-[1.05]">
                        Work with the{" "} 
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">Web3 Leaders</span>
                    </h1>
                    <p className="text-xl md:text-2xl text-blue-100/70 max-w-3xl mx-auto mb-16 leading-relaxed font-light">
                        Build the next generation of decentralized ecosystems. We're looking for passionate creators to join our mission in Jaipur and worldwide.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-6">
                        <button 
                            onClick={() => document.getElementById('openings').scrollIntoView({behavior: 'smooth'})}
                            className="group px-12 py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black text-base uppercase tracking-wider shadow-2xl shadow-blue-600/30 transition-all hover:-translate-y-1 active:scale-95"
                        >
                            Open Positions
                        </button>
                        <button 
                            onClick={() => document.getElementById('application-form').scrollIntoView({behavior: 'smooth'})}
                            className="px-12 py-5 bg-white/5 hover:bg-white/10 text-white border border-white/20 rounded-2xl font-black text-base uppercase tracking-wider backdrop-blur-md transition-all hover:-translate-y-1"
                        >
                            Get in Touch
                        </button>
                    </div>
                </div>
            </section>

            {/* Why Join Us */}
            <section className="py-24 container mx-auto px-4 relative">
                <div className="text-center mb-20">
                    <h2 className="text-5xl md:text-6xl text-center font-black text-slate-900 mb-6">Why Meta Block?</h2>
                    <div className="w-24 h-2 bg-gradient-to-r from-blue-600 to-indigo-600 mx-auto rounded-full shadow-lg"></div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-6xl mx-auto">
                    {[
                        { icon: <FaRocket />, title: "Hyper Growth", desc: "Scale with the industry leaders in Metaverse, Blockchain, and AI development." },
                        { icon: <FaUsers />, title: "Elite Culture", desc: "Join a vibrant community of elite developers and strategic thinkers." },
                        { icon: <FaBriefcase />, title: "Remote Friendly", desc: "Enjoy flexibility with our hybrid and remote-first work environment." }
                    ].map((item, index) => (
                        <div key={index} className="group bg-white hover:bg-slate-50 p-12 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/50 transition-all duration-500 hover:-translate-y-2" style={{textAlign:'left'}}>
                            <div style={{width:'72px', height:'72px', borderRadius:'20px', background:'#2563eb', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'28px', fontSize:'2rem', color:'white'}} className="transition-transform duration-500 group-hover:scale-110">
                                {item.icon}
                            </div>
                            <h3 className="text-3xl font-extrabold text-slate-900 mb-4">{item.title}</h3>
                            <p className="text-lg text-slate-600 leading-relaxed font-medium">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Openings Grid */}
            <section id="openings" className="py-24 bg-slate-100/80 rounded-t-[4rem] md:rounded-t-[6rem]">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6 max-w-6xl mx-auto">
                        <div className="text-left">
                            <h2 className="text-5xl font-black text-slate-900 mb-3 tracking-tight">Active Openings</h2>
                            <p className="text-xl text-slate-600 font-medium italic">"Great things never come from comfort zones."</p>
                        </div>
                        <div className="bg-white p-2 rounded-2xl shadow-xl flex gap-2">
                             <span className="px-8 py-4 bg-blue-600 text-white rounded-xl text-sm font-black uppercase shadow-lg shadow-blue-600/20">All Departments</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        {jobs.map((job) => (
                            <div key={job.id} style={{textAlign: 'left'}} className="group bg-white hover:bg-blue-50/30 p-8 rounded-3xl border border-slate-200 flex flex-row gap-6 items-start transition-all duration-500 hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-200/50">
                                <div style={{width:'64px', height:'64px', minWidth:'64px', borderRadius:'14px', background:'#1d4ed8', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'1.8rem', color:'white', boxShadow:'0 4px 14px rgba(29,78,216,0.35)'}} className="group-hover:scale-110 transition-all duration-500">
                                    {job.icon}
                                </div>
                                <div className="flex-1">
                                    <div style={{display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap'}}>
                                        <span style={{padding: '4px 14px', background: '#dbeafe', color: '#1d4ed8', fontSize: '11px', fontWeight: 800, borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '0.08em'}}>{job.category}</span>
                                        <span style={{padding: '4px 14px', background: '#f1f5f9', color: '#475569', fontSize: '11px', fontWeight: 800, borderRadius: '999px', textTransform: 'uppercase', letterSpacing: '0.08em'}}>{job.type}</span>
                                    </div>
                                    <h3 style={{fontSize: '1.4rem', fontWeight: 900, color: '#0f172a', marginBottom: '8px'}}>{job.title}</h3>
                                    <div style={{display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap'}}>
                                        <span style={{display: 'flex', alignItems: 'center', gap: '6px', color: '#2563eb', fontSize: '13px', fontWeight: 700}}><FaBriefcase /> {job.experience}</span>
                                        <span style={{color: '#94a3b8', fontSize: '13px', fontWeight: 700}}>{job.location}</span>
                                    </div>
                                    <p style={{color: '#475569', fontSize: '15px', lineHeight: '1.7', marginBottom: '20px'}}>{job.description}</p>
                                    <button 
                                        onClick={() => scrollToForm(job.title)}
                                        style={{padding: '12px 28px', background: '#0f172a', color: 'white', borderRadius: '12px', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em', border: 'none', cursor: 'pointer', transition: 'all 0.3s'}}
                                        onMouseEnter={e => e.target.style.background='#2563eb'}
                                        onMouseLeave={e => e.target.style.background='#0f172a'}
                                    >
                                        Apply for this Role
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Form Section */}
            <section id="application-form" className="py-32 container mx-auto px-4">
                <div className="max-w-7xl mx-auto bg-white rounded-[3rem] overflow-hidden flex flex-col lg:flex-row shadow-[0_32px_128px_-16px_rgba(0,0,0,0.12)] border border-slate-100">
                    <div className="lg:w-[40%] bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 p-10 md:p-14 text-white flex flex-col justify-between relative overflow-hidden">
                        {/* Decorative circles */}
                        <div className="absolute -top-16 -right-16 w-56 h-56 bg-white/5 rounded-full"></div>
                        <div className="absolute -bottom-20 -left-10 w-72 h-72 bg-indigo-500/10 rounded-full"></div>

                        <div className="relative z-10">
                            {/* Badge */}
                            <span style={{display:'inline-block', padding:'6px 18px', background:'rgba(255,255,255,0.15)', borderRadius:'999px', fontSize:'11px', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'24px', backdropFilter:'blur(8px)', border:'1px solid rgba(255,255,255,0.2)'}}>
                                🚀 We're Hiring
                            </span>

                            <h2 style={{fontSize:'2.4rem', fontWeight:900, lineHeight:1.1, marginBottom:'16px'}}>
                                Join the<br/>Innovation<br/>Fast-lane
                            </h2>
                            <p style={{color:'rgba(219,234,254,0.85)', fontSize:'1rem', lineHeight:1.7, marginBottom:'32px', fontWeight:400}}>
                                Tell us who you are and what makes you brilliant. We're building the future of Web3 — and we want you on the team.
                            </p>

                            {/* Perks list */}
                            <div style={{marginBottom:'32px'}}>
                                <p style={{fontSize:'11px', fontWeight:800, letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(147,197,253,1)', marginBottom:'14px'}}>What You Get</p>
                                {[
                                    '💰 Competitive Salary & Bonuses',
                                    '🏡 Remote / Hybrid Work Options',
                                    '📈 Fast-Track Career Growth',
                                    '🎓 Learning & Certifications Budget',
                                    '🌍 Work on Global Web3 Projects',
                                ].map((perk, i) => (
                                    <div key={i} style={{display:'flex', alignItems:'center', gap:'10px', marginBottom:'10px'}}>
                                        <div style={{width:'8px', height:'8px', borderRadius:'999px', background:'#60a5fa', flexShrink:0}}></div>
                                        <span style={{fontSize:'14px', fontWeight:500, color:'rgba(239,246,255,0.9)'}}>{perk}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Divider */}
                            <div style={{height:'1px', background:'rgba(255,255,255,0.1)', marginBottom:'28px'}}></div>

                            {/* Contact cards */}
                            <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
                                <div style={{display:'flex', alignItems:'center', gap:'12px', background:'rgba(255,255,255,0.12)', padding:'12px 16px', borderRadius:'14px'}}>
                                    <div style={{width:'40px', height:'40px', minWidth:'40px', borderRadius:'12px', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
                                        <FaPaperPlane size={16} color="white"/>
                                    </div>
                                    <div style={{minWidth:0}}>
                                        <p style={{fontSize:'9px', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(147,197,253,1)', marginBottom:'2px'}}>Email Us</p>
                                        <p style={{fontSize:'12px', fontWeight:600, color:'white', wordBreak:'break-all', lineHeight:1.4}}>info@metablocktechnologies.in</p>
                                    </div>
                                </div>
                                <div style={{display:'flex', alignItems:'center', gap:'12px', background:'rgba(255,255,255,0.12)', padding:'12px 16px', borderRadius:'14px'}}>
                                    <div style={{width:'40px', height:'40px', minWidth:'40px', borderRadius:'12px', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
                                        <FaBriefcase size={16} color="white"/>
                                    </div>
                                    <div style={{minWidth:0}}>
                                        <p style={{fontSize:'9px', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(147,197,253,1)', marginBottom:'2px'}}>Location</p>
                                        <p style={{fontSize:'13px', fontWeight:600, color:'white'}}>Jaipur, India &amp; Remote</p>
                                    </div>
                                </div>
                                <div style={{display:'flex', alignItems:'center', gap:'12px', background:'rgba(255,255,255,0.12)', padding:'12px 16px', borderRadius:'14px'}}>
                                    <div style={{width:'40px', height:'40px', minWidth:'40px', borderRadius:'12px', background:'rgba(255,255,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0}}>
                                        <FaUsers size={16} color="white"/>
                                    </div>
                                    <div style={{minWidth:0}}>
                                        <p style={{fontSize:'9px', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(147,197,253,1)', marginBottom:'2px'}}>Team Size</p>
                                        <p style={{fontSize:'13px', fontWeight:600, color:'white'}}>50+ Innovators &amp; Growing</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="lg:w-[65%] p-10 md:p-16 lg:p-16">
                        <form onSubmit={handleSubmit} className="space-y-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                <div className="space-y-3">
                                    <label style={{color:'#1e293b'}} className="text-sm font-black uppercase tracking-widest ml-1">Full Name</label>
                                    <input 
                                        type="text" required name="name" value={formData.name} onChange={handleInputChange}
                                        placeholder="Shashank Goyal"
                                        style={{color:'#0f172a'}}
                                        className="w-full px-8 py-5 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-300 font-bold text-lg placeholder:text-slate-300"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label style={{color:'#1e293b'}} className="text-sm font-black uppercase tracking-widest ml-1">Work Email</label>
                                    <input 
                                        type="email" required name="email" value={formData.email} onChange={handleInputChange}
                                        placeholder="shashank@example.com"
                                        style={{color:'#0f172a'}}
                                        className="w-full px-8 py-5 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-300 font-bold text-lg placeholder:text-slate-300"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                <div className="space-y-3">
                                    <label style={{color:'#1e293b'}} className="text-sm font-black uppercase tracking-widest ml-1">Mobile Number</label>
                                    <input 
                                        type="tel" required name="phone" value={formData.phone} onChange={handleInputChange}
                                        placeholder="+91 98765 43210"
                                        style={{color:'#0f172a'}}
                                        className="w-full px-8 py-5 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-300 font-bold text-lg placeholder:text-slate-300"
                                    />
                                </div>
                                <div className="space-y-3" ref={dropdownRef} style={{position:'relative'}}>
                                    <label className="text-sm font-black text-slate-800 uppercase tracking-widest ml-1">Select Role</label>

                                    {/* Custom Dropdown Trigger */}
                                    <div
                                        onClick={() => setDropdownOpen(!dropdownOpen)}
                                        style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 28px', borderRadius:'16px', border: dropdownOpen ? '2px solid #3b82f6' : '2px solid #f1f5f9', background: dropdownOpen ? 'white' : '#f8fafc', cursor:'pointer', userSelect:'none', transition:'all 0.2s'}}
                                    >
                                        <span style={{fontSize:'17px', fontWeight:600, color: formData.position ? '#0f172a' : '#94a3b8'}}>
                                            {formData.position || '— Choose a Role —'}
                                        </span>
                                        <svg style={{width:'20px', height:'20px', color:'#64748b', transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform 0.2s'}} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg>
                                    </div>

                                    {/* Hidden native select for form validation */}
                                    <select required name="position" value={formData.position} onChange={handleInputChange} style={{position:'absolute', opacity:0, width:0, height:0, pointerEvents:'none'}}>
                                        <option value=""></option>
                                        {roleGroups.map(g => g.options.map(o => <option key={o} value={o}>{o}</option>))}
                                    </select>

                                    {/* Dropdown List */}
                                    {dropdownOpen && (
                                        <div style={{position:'absolute', top:'100%', left:0, right:0, marginTop:'6px', background:'white', borderRadius:'16px', border:'1px solid #e2e8f0', boxShadow:'0 20px 60px -10px rgba(0,0,0,0.15)', zIndex:999, maxHeight:'280px', overflowY:'auto'}}>
                                            {roleGroups.map((group, gi) => (
                                                <div key={gi}>
                                                    <div style={{padding:'10px 20px 6px', fontSize:'11px', fontWeight:800, letterSpacing:'0.1em', textTransform:'uppercase', color:'#94a3b8', borderTop: gi > 0 ? '1px solid #f1f5f9' : 'none'}}>
                                                        {group.label}
                                                    </div>
                                                    {group.options.map((opt, oi) => (
                                                        <div
                                                            key={oi}
                                                            onClick={() => { setFormData(prev => ({...prev, position: opt})); setDropdownOpen(false); }}
                                                            style={{padding:'11px 20px 11px 28px', fontSize:'15px', fontWeight: formData.position === opt ? 700 : 500, color: formData.position === opt ? '#2563eb' : '#1e293b', cursor:'pointer', transition:'background 0.15s, color 0.15s', background: formData.position === opt ? '#eff6ff' : 'transparent'}}
                                                            onMouseEnter={e => { e.currentTarget.style.background='#f1f5f9'; e.currentTarget.style.color='#0f172a'; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = formData.position === opt ? '#eff6ff' : 'transparent'; e.currentTarget.style.color = formData.position === opt ? '#2563eb' : '#1e293b'; }}
                                                        >
                                                            {opt}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label style={{color:'#1e293b'}} className="text-sm font-black uppercase tracking-widest ml-1">Portfolio (LinkedIn/GitHub)</label>
                                <input 
                                    type="url" name="linkedin" value={formData.linkedin} onChange={handleInputChange}
                                    placeholder="https://github.com/yourprofile"
                                    style={{color:'#0f172a'}}
                                    className="w-full px-8 py-5 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-300 font-bold text-lg placeholder:text-slate-300"
                                />
                            </div>

                            <div className="space-y-3">
                                <label style={{color:'#1e293b'}} className="text-sm font-black uppercase tracking-widest ml-1">Cover Note</label>
                                <textarea 
                                    rows="4" name="message" value={formData.message} onChange={handleInputChange}
                                    placeholder="Share your proudest achievement..."
                                    style={{color:'#0f172a'}}
                                    className="w-full px-8 py-5 rounded-2xl border-2 border-slate-100 bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all duration-300 font-bold text-lg placeholder:text-slate-300"
                                ></textarea>
                            </div>

                            <button 
                                type="submit"
                                disabled={loading}
                                className={`w-full py-7 rounded-[2.5rem] font-black text-base uppercase tracking-[0.2em] text-white transition-all duration-500 transform active:scale-[0.97] flex items-center justify-center gap-4 ${loading ? 'bg-slate-300 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:shadow-[0_20px_50px_-10px_rgba(37,99,235,0.5)]'}`}
                            >
                                {loading ? (
                                    <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <span className="flex items-center gap-3">Send My Application <FaPaperPlane className="text-xs"/></span>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Career;
